import { b } from "@/b";
import { Text, Flex, Box } from "@chakra-ui/react";
import { FragmentData, OutputOf } from "@gqlb/core";
import React, { useEffect, useRef, createContext, useContext } from "react";

export const Macros_Fragment = b.fragment("Macros", "Macros", (b) => [
  b.calories(),
  b.carbs(),
  b.fat(),
  b.protein(),
]);

const Macro: React.FC<{
  label: string;
  value: number;
  unit?: string;
  digits?: number;
  size?: "sm" | "lg";
}> = ({ label, value, unit, digits = 1, size = "lg" }) => {
  const digitSpan = useRef<HTMLSpanElement>(null);

  const animationDuration = 500;

  useEffect(() => {
    if (digitSpan.current) {
      let frame: number | null = null;

      let lastFrame = Date.now();

      const text = digitSpan.current.innerText;
      const start = parseFloat(text);

      let current = start;
      const delta = value - current;

      let stepPerMs = delta / animationDuration;

      const animate = () => {
        const currentFrame = Date.now();

        const deltaT = currentFrame - lastFrame;
        const step = stepPerMs * deltaT;
        current += step;
        lastFrame = currentFrame;

        if (digitSpan.current) {
          if (step === 0) {
            frame = requestAnimationFrame(animate);
          } else if (
            (step > 0 && current < value) ||
            (step < 0 && current > value)
          ) {
            digitSpan.current.innerText = current.toFixed(digits);
            frame = requestAnimationFrame(animate);
          } else {
            digitSpan.current.innerText = value.toFixed(digits);
          }
        }
      };
      animate();
      return () => {
        if (frame) {
          cancelAnimationFrame(frame);
        }
      };
    }
  }, [value, digits]);

  function s<T>(sm: T, lg: T): T {
    return size === "sm" ? sm : lg;
  }

  return (
    <Flex
      pos="relative"
      alignItems="center"
      justifyContent="center"
      pt={s(3, 2)}
      minW={s(12, 20)}
      border={s("none", "1px solid #ccc")}
      borderRadius="md"
      overflow={s("hidden", "visible")}
    >
      <Text
        fontSize={s("10px", "xs")}
        fontWeight="medium"
        textAlign="center"
        color="gray.500"
        bg="white"
        top={s("0", "-2")}
        insetX={s("0", "auto")}
        pos="absolute"
        px={s(0, 2)}
      >
        {label}
      </Text>
      <Text fontSize={s("xs", "xl")}>
        <Text as="span" ref={digitSpan}>
          {(0).toFixed(digits)}
        </Text>
        {unit ?? ""}
      </Text>
    </Flex>
  );
};

export const Macros: React.FC<{
  data: OutputOf<typeof Macros_Fragment>;
  size?: "sm" | "lg";
}> = ({ data, size }) => {
  return (
    <Box
      sx={{
        containerType: "inline-size",
        containerName: "macros",
        "@container macros (min-width: 0)": {
          "[data-macros]": {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          },
        },
        "@container macros (min-width: 180px)": {
          "[data-macros]": {
            display: "flex",
          },
        },
      }}
      w="full"
    >
      <Box
        data-macros
        flexDir="row"
        alignItems="center"
        justifyContent="space-between"
        rowGap="2"
        px="2"
      >
        <Macro size={size} label="Calories" value={data.calories} digits={0} />
        <Macro size={size} label="Fat" value={data.fat} unit="g" />
        <Macro size={size} label="Carbs" value={data.carbs} unit="g" />
        <Macro size={size} label="Protein" value={data.protein} unit="g" />
      </Box>
    </Box>
  );
};
